// Monthly climate normals for the city guides' "Weather by month" table and
// its FAQ entries (src/components/riviera/city-climate*). Static on purpose:
// normals change once a decade, so there is no API, DB column or build step —
// update the numbers here when Météo-France publishes the next period.
//
// Air temperature (mean daily max/min, °C) and days with ≥ 1 mm of rain are
// Météo-France station normals. Not every town has its own station: Menton
// reads Monaco's, Villefranche-sur-Mer Nice airport's, and Saint-Tropez
// Cogolin's (the nearest with published normals, a few km inland — so its
// summer highs run a little hotter than the harbour's). The station label shown
// under the table names which one, per locale (cityClimate.stations.*).

export type MonthClimate = {
  /** Mean daily maximum, °C. */
  high: number;
  /** Mean daily minimum, °C. */
  low: number;
  /** Mean number of days with ≥ 1 mm of precipitation. */
  rainDays: number;
};

export type CityClimate = {
  /** Normals period, e.g. "1991–2020". */
  period: string;
  /** January → December. */
  months: MonthClimate[];
};

/**
 * Approximate monthly mean sea-surface temperature off the coast, °C, Jan → Dec.
 * One series for every town: there are no per-town sea normals, and the
 * column is labelled approximate under the table. The FAQ's static swimming answer
 * (cityClimate.faq.swimA) quotes these numbers — keep the two in sync.
 */
export const SEA_TEMPS = [13.4, 13.0, 13.4, 14.6, 18.0, 21.8, 23.1, 23.6, 22.2, 19.6, 17.4, 14.9];

function months(rows: [high: number, low: number, rainDays: number][]): MonthClimate[] {
  return rows.map(([high, low, rainDays]) => ({ high, low, rainDays }));
}

// Météo-France 06088001, Nice-Côte d'Azur airport.
const NICE: CityClimate = {
  period: '1991–2020',
  months: months([
    [13.3, 5.8, 5.8],
    [13.5, 6.1, 5.1],
    [15.4, 8.3, 4.9],
    [17.4, 10.8, 6.4],
    [21.0, 14.5, 5.1],
    [24.7, 18.1, 3.8],
    [27.5, 20.8, 1.8],
    [27.9, 21.1, 2.3],
    [24.8, 17.7, 4.9],
    [21.0, 14.0, 7.5],
    [17.0, 9.7, 8.5],
    [14.1, 6.6, 6.0],
  ]),
};

// Météo-France Monaco station, 1981–2010 normals.
const MONACO: CityClimate = {
  period: '1981–2010',
  months: months([
    [13.0, 7.4, 6.0],
    [13.0, 7.4, 4.9],
    [14.9, 9.1, 4.5],
    [16.7, 10.9, 7.3],
    [20.4, 14.6, 5.5],
    [23.7, 18.0, 4.1],
    [26.6, 21.0, 1.7],
    [26.9, 21.4, 2.5],
    [24.0, 18.3, 5.1],
    [20.6, 15.2, 7.3],
    [16.5, 11.2, 7.1],
    [13.9, 8.5, 6.5],
  ]),
};

const CLIMATE: Record<string, CityClimate> = {
  nice: NICE,
  villefranche: NICE,
  monaco: MONACO,
  menton: MONACO,
  // Météo-France Cannes station.
  cannes: {
    period: '1991–2020',
    months: months([
      [13.6, 3.7, 5.9],
      [13.9, 3.7, 5.3],
      [16.0, 6.0, 5.7],
      [18.2, 8.7, 6.3],
      [21.9, 12.6, 5.4],
      [25.5, 16.3, 3.6],
      [28.2, 18.7, 1.8],
      [28.6, 18.8, 2.7],
      [25.3, 15.4, 4.8],
      [21.3, 12.1, 7.5],
      [17.1, 7.9, 8.5],
      [14.3, 4.6, 6.4],
    ]),
  },
  // Météo-France Antibes station.
  antibes: {
    period: '1991–2020',
    months: months([
      [13.5, 4.2, 6.3],
      [13.9, 4.3, 5.1],
      [16.2, 6.5, 5.2],
      [18.5, 9.0, 6.8],
      [22.3, 12.8, 4.9],
      [26.3, 16.7, 3.6],
      [29.1, 19.1, 1.8],
      [29.6, 19.1, 2.5],
      [25.9, 15.7, 4.8],
      [21.6, 12.4, 7.4],
      [17.2, 8.2, 8.6],
      [14.3, 5.0, 6.2],
    ]),
  },
  // Météo-France 83042001, Cogolin.
  sttropez: {
    period: '1991–2020',
    months: months([
      [13.6, 2.7, 6.4],
      [14.4, 2.5, 6.0],
      [17.3, 4.8, 5.7],
      [20.2, 7.5, 6.7],
      [24.3, 10.9, 5.1],
      [29.1, 14.3, 2.6],
      [32.0, 16.5, 1.1],
      [32.2, 16.4, 2.0],
      [27.6, 13.3, 4.1],
      [22.5, 10.8, 7.2],
      [17.2, 6.5, 8.9],
      [14.1, 3.7, 7.1],
    ]),
  },
};

/** A city's monthly normals, or undefined for a city without data. */
export function cityClimate(cityId: string): CityClimate | undefined {
  return CLIMATE[cityId];
}

/**
 * Months (0 = January) that get their own "What is the weather like in {city}
 * in {month}?" FAQ entry — the shoulder months people plan trips around and
 * search for by name, rather than all twelve.
 */
export const CLIMATE_FAQ_MONTHS = [4, 9];

/** Index of the month with the largest `pick` value (first one on a tie). */
export function maxMonth(climate: CityClimate, pick: (month: MonthClimate) => number): number {
  return climate.months.reduce(
    (best, month, index) => (pick(month) > pick(climate.months[best]) ? index : best),
    0,
  );
}

/** Index of the month with the smallest `pick` value (first one on a tie). */
export function minMonth(climate: CityClimate, pick: (month: MonthClimate) => number): number {
  return maxMonth(climate, (month) => -pick(month));
}
